// ---------------------------------------------------------------------------
// EvaluateFormColumn — the left-hand form for the agent Evaluate tab: the
// pinned-agent card plus the reused eval-template cards (query/data set,
// graders, variables, concurrency). Presentational — all state lives in
// AgentEvaluateTab and is threaded in via the form-field hook bundles.
// ---------------------------------------------------------------------------

import {
  classifyBindingFlowDiagnostics,
  type BindingFlowAnalysis,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindingFlow.utils.js';
import type { BindingRow } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindings.utils.js';
import { BindingFlowPanel } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/components/BindingFlowPanel/BindingFlowPanel.js';
import { ConcurrencyCard } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/components/ConcurrencyCard/ConcurrencyCard.js';
import { GradersList } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/components/GradersList/GradersList.js';
import { QueryDataSetCards } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/components/QueryDataSetCards/QueryDataSetCards.js';
import type {
  GraderSummary,
  TaskDraft,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.types.js';
import type { FileSource } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useFileSource.js';
import type { UseFormFieldsResult } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useFormFields.js';

import { useStyles } from './EvaluateFormColumn.style.js';

export interface EvaluateFormColumnProps {
  agentName: string;
  disabled: boolean;
  fields: UseFormFieldsResult;
  /** Per-variable runtime bindings (name → $expr), one row per declared variable. */
  bindings: BindingRow[];
  bindingFlow: BindingFlowAnalysis;
  onBindingChange: (name: string, expr: string) => void;
  queryFile: FileSource;
  dataFile: FileSource;
  task: TaskDraft;
  uniqueGraders: GraderSummary[];
  gradersLoading: boolean;
  onAddGraderRow: (name: string) => void;
  onRemoveGraderRow: (rowId: string) => void;
  runError: string | null;
}

export function EvaluateFormColumn({
  agentName,
  disabled,
  fields,
  bindings,
  bindingFlow,
  onBindingChange,
  queryFile,
  dataFile,
  task,
  uniqueGraders,
  gradersLoading,
  onAddGraderRow,
  onRemoveGraderRow,
  runError,
}: EvaluateFormColumnProps) {
  const styles = useStyles();
  const diagnostics = classifyBindingFlowDiagnostics(bindingFlow);

  return (
    <div className={styles.formColumn}>
      <div className={styles.cards}>
        {/* Pinned agent — fixed to this page, not selectable. */}
        <section className={styles.card} aria-labelledby="agent-eval-agent">
          <div id="agent-eval-agent" className={styles.cardLabel}>
            Agent
          </div>
          <div className={styles.cardBody}>
            <span className={styles.agentName}>{agentName}</span>
          </div>
        </section>

        <fieldset disabled={disabled} className={styles.fieldsetReset}>
          <QueryDataSetCards
            viewOnly={false}
            querySetMode={fields.querySetMode}
            onQuerySetModeChange={fields.setQuerySetMode}
            queryInlineText={fields.queryInlineText}
            onQueryInlineTextChange={fields.setQueryInlineText}
            queryFileName={queryFile.name}
            queryFileItemCount={queryFile.items?.length ?? null}
            queryFileAnalysis={queryFile.analysis}
            queryFileError={queryFile.error}
            queryErrors={diagnostics.querySet}
            onQueryFile={queryFile.handle}
            dataSetEnabled={fields.dataSetEnabled}
            onDataSetEnabledChange={fields.setDataSetEnabled}
            dataSetMode={fields.dataSetMode}
            onDataSetModeChange={fields.setDataSetMode}
            dataInlineText={fields.dataInlineText}
            onDataInlineTextChange={fields.setDataInlineText}
            dataFileName={dataFile.name}
            dataFileItemCount={dataFile.items?.length ?? null}
            dataFileAnalysis={dataFile.analysis}
            dataFileError={dataFile.error}
            dataErrors={diagnostics.dataSet}
            onDataFile={dataFile.handle}
            dataSetExistingId={fields.dataSetExistingId}
            dataSetExistingVersion={fields.dataSetExistingVersion}
            onDataSetExistingChange={(id, version) => {
              if (id !== fields.dataSetExistingId)
                fields.setDataSetFileSelector([]);
              fields.setDataSetExistingId(id);
              fields.setDataSetExistingVersion(version);
            }}
            dataSetFileSelector={fields.dataSetFileSelector}
            onDataSetFileSelectorChange={fields.setDataSetFileSelector}
          />

          <section className={styles.card} aria-labelledby="agent-eval-graders">
            <div id="agent-eval-graders" className={styles.cardLabel}>
              Graders
            </div>
            <GradersList
              task={task}
              uniqueGraders={uniqueGraders}
              gradersLoading={gradersLoading}
              viewOnly={false}
              onAddGraderRow={onAddGraderRow}
              onRemoveGraderRow={onRemoveGraderRow}
            />
          </section>

          <BindingFlowPanel
            analysis={bindingFlow}
            bindings={bindings}
            onBindingChange={onBindingChange}
            onVariableUpdate={fields.updateVariable}
            onVariableAdd={fields.addVariable}
            onVariableRemove={fields.removeVariable}
            disabled={disabled}
          />

          <ConcurrencyCard
            workersText={fields.workersText}
            onWorkersChange={fields.setWorkersText}
            parallelism={fields.parallelism}
            onParallelismChange={fields.setParallelism}
          />

          {runError !== null && (
            <div className={styles.errorText} role="alert">
              {runError}
            </div>
          )}
        </fieldset>
      </div>
    </div>
  );
}
